import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Check, X, AlertTriangle, ThumbsUp, ThumbsDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface AILearningFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  extractedData: any;
  billId?: string;
  queueItemId?: string;
  onFeedbackSubmitted: () => void;
}

export function AILearningFeedbackDialog({
  open,
  onOpenChange,
  extractedData,
  billId,
  queueItemId,
  onFeedbackSubmitted,
}: AILearningFeedbackDialogProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [feedbackType, setFeedbackType] = useState<'correction' | 'validation' | null>(null);
  const [correctedData, setCorrectedData] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFieldCorrection = (field: string, value: string) => {
    setCorrectedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const submitFeedback = async () => {
    setIsSubmitting(true);
    try {
      const feedbackPayload = {
        user_id: user?.id,
        feedback_type: feedbackType,
        original_extraction: extractedData,
        corrected_extraction: feedbackType === 'correction' ? correctedData : extractedData,
        confidence_before: extractedData.ai_confidence || 0,
        bill_id: billId,
        queue_item_id: queueItemId,
        field_corrections: feedbackType === 'correction' ? correctedData : {}
      };

      // Import and use the hook
      const { data, error } = await supabase
        .from('ai_learning_feedback')
        .insert(feedbackPayload);

      if (error) throw error;

      toast({
        title: "Feedback Submitted",
        description: "Thank you! Your feedback will help improve AI accuracy.",
      });

      onFeedbackSubmitted();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit feedback. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderExtractionField = (label: string, field: string, value: any) => (
    <div key={field} className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          value={feedbackType === 'correction' ? (correctedData[field] ?? value) : value}
          onChange={(e) => handleFieldCorrection(field, e.target.value)}
          readOnly={feedbackType !== 'correction'}
          className={feedbackType === 'correction' ? 'border-amber-300' : ''}
        />
        {feedbackType === 'validation' && (
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50"
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            AI Learning Feedback
          </DialogTitle>
        </DialogHeader>

        {!feedbackType ? (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Help improve our AI by providing feedback on the extracted data:
            </p>
            
            <div className="grid gap-3">
              <Card 
                className="cursor-pointer hover:bg-accent transition-colors"
                onClick={() => setFeedbackType('validation')}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ThumbsUp className="h-4 w-4 text-emerald-600" />
                    Validate Extraction
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground">
                    Mark each field as correct or incorrect
                  </p>
                </CardContent>
              </Card>

              <Card 
                className="cursor-pointer hover:bg-accent transition-colors"
                onClick={() => setFeedbackType('correction')}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ThumbsDown className="h-4 w-4 text-amber-600" />
                    Correct Extraction
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground">
                    Provide corrected values for incorrect fields
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {feedbackType === 'validation' ? 'Validation Mode' : 'Correction Mode'}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFeedbackType(null)}
              >
                Change Mode
              </Button>
            </div>

            <div className="space-y-4">
              <div className="grid gap-4">
                {renderExtractionField('Customer Name', 'customer_name', extractedData.customer_name_raw)}
                {renderExtractionField('Account Number', 'account_number', extractedData.account_number_raw)}
                {renderExtractionField('Amount', 'amount', extractedData.amount)}
                {renderExtractionField('Bill Date', 'bill_date', extractedData.bill_date)}
                {renderExtractionField('Supplier', 'supplier_name', extractedData.supplier_name)}
                {renderExtractionField('Service Type', 'service_type', extractedData.service_type)}
              </div>

              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea 
                  placeholder="Any additional feedback about the extraction quality..."
                  className="min-h-[80px]"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={submitFeedback}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}